import { toast } from "sonner";
import { words3Letters } from "@/lib/words3Letters";
import { words4Letters } from "@/lib/words4Letters";
import { words5Letters } from "@/lib/words5Letters";
import { words6Letters } from "@/lib/words6Letters";

export const validateGuessInput = (currentGuess: string, wordLength: number): boolean => {
  if (currentGuess.length === 0) {
    toast.error("Please enter a word", {
      position: "top-center",
      className: "text-lg",
    });
    return false;
  }

  if (currentGuess.length !== wordLength) {
    toast.error(`Word must be ${wordLength} letters long`, {
      position: "top-center",
      className: "text-lg",
    });
    return false;
  }

  return true;
};

const getWordList = (wordLength: number): string[] => {
  switch (wordLength) {
    case 3:
      return words3Letters;
    case 4:
      return words4Letters;
    case 5:
      return words5Letters;
    case 6:
      return words6Letters;
    default:
      return [];
  }
};

export const validateWord = async (word: string): Promise<boolean> => {
  const wordLength = word.length;
  const wordList = getWordList(wordLength);
  const isValid = wordList.includes(word.toLowerCase());

  if (!isValid) {
    toast.error("Not a valid word", {
      position: "top-center",
      className: "text-lg",
    });
    return false;
  }

  return true;
};

export const validateDuplicateGuess = (
  currentGuess: string,
  previousGuesses: { letter: string; status: string }[][]
): boolean => {
  const isDuplicate = previousGuesses.some(guess => 
    guess.map(g => g.letter).join('').toLowerCase() === currentGuess.toLowerCase()
  );

  if (isDuplicate) {
    toast.error("You've already tried this word!", {
      position: "top-center",
      className: "text-lg",
    });
    return true;
  }

  return false;
};

export const validateGuess = (guess: string, answer: string): GuessResult => {
  if (!answer) {
    console.error('Answer is undefined in validateGuess');
    return [];
  }

  const guessLetters = guess.split('');
  const answerLetters = answer.split('');
  
  const result = guessLetters.map((letter) => ({
    letter,
    status: "incorrect" as "correct" | "wrong-position" | "incorrect"
  }));

  const remainingAnswerLetters = [...answerLetters];

  // First pass: Mark correct positions
  result.forEach((entry, index) => {
    if (entry.letter.toLowerCase() === remainingAnswerLetters[index]?.toLowerCase()) {
      entry.status = "correct";
      remainingAnswerLetters[index] = null;
    }
  });

  // Second pass: Mark misplaced letters
  result.forEach((entry, index) => {
    if (entry.status === "incorrect") {
      const matchIndex = remainingAnswerLetters.findIndex(
        (letter) => letter?.toLowerCase() === entry.letter.toLowerCase()
      );
      if (matchIndex !== -1) {
        entry.status = "wrong-position";
        remainingAnswerLetters[matchIndex] = null;
      }
    }
  });

  return result;
};

type GuessResult = {
  letter: string;
  status: "correct" | "wrong-position" | "incorrect";
}[];